/** Lifecycle Innovations — Bridge4IP reseller. One webhook for the whole tree. */
export const LIFECYCLE_RESELLER_ID = 1170;
/**
 * Shared reader for the per-ICCID OCS event ring-buffer in OCS_EVENT_ROUTING KV
 * (same layout as apps/mcp-server/src/ocs-webhook.ts → storeEvent).
 */

/** Minimal KV surface (Cloudflare KV–compatible). */
export interface OcsEventRoutingKv {
  get(key: string, type: "json"): Promise<unknown>;
  get(key: string, type: "text"): Promise<string | null>;
  list(options: { prefix: string; cursor?: string }): Promise<{
    keys: Array<{ name: string }>;
    list_complete: boolean;
    cursor?: string;
  }>;
}

interface OcsEventStored {
  event_id: string;
  event_type: string;
  iccid: string;
  occurred_at: number;
  payload: Record<string, unknown>;
  subscriber_id?: number;
  account_id?: number;
  reseller_id?: number;
}

interface IccidRoutingEntry {
  reseller_id: number;
  user_key: string;
  account_id?: number;
}

interface StoredEventBatch {
  events: OcsEventStored[];
  updated_at: string;
}

export interface OcsEventOutput {
  event_id: string;
  event_type: string;
  timestamp: string;
  subscriber_id?: number;
  account_id?: number;
  reseller_id?: number;
  data: Record<string, unknown>;
}

export interface ListRecentOcsEventsResult {
  iccid: string;
  events: OcsEventOutput[];
  total_in_buffer: number;
  filtered_count: number;
  buffer_oldest_event_timestamp: string | null;
  buffer_newest_event_timestamp: string | null;
}

/**
 * Normalises MSISDN / IMSI / digit references into a 19–20 digit ring-buffer ICCID
 * key (matches Bridge4IP Relay LU `toBufferKey` in bridge4ip-webhook.ts).
 */
export function ocsEventRingBufferIccidFromDigitsReference(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) {
    throw new Error("reference must contain at least one digit");
  }
  if (digits.length > 20) {
    return digits.slice(-20);
  }
  if (digits.length >= 19) {
    return digits;
  }
  return digits.padStart(19, "0");
}

/** MSISDN from a getSubscriber-style payload (`msisdn` / `MSISDN`), for ring-buffer key lookup. */
export function msisdnFieldFromSubscriberPayload(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const r = data as Record<string, unknown>;
  const v = r["msisdn"] ?? r["MSISDN"];
  if (v === undefined || v === null) return null;
  if (typeof v === "string") return /\d/.test(v) ? v : null;
  if (typeof v === "number") return String(v);
  return null;
}

/** Merge multiple newest-first event slices; de-dupe by event_id; cap at `limit`. */
export function mergeOcsEventOutputsNewestFirst(
  parts: readonly OcsEventOutput[][],
  limit: number,
): OcsEventOutput[] {
  const seen = new Set<string>();
  const out: OcsEventOutput[] = [];
  const combined = [...parts]
    .flat()
    .sort((x, y) =>
      x.timestamp < y.timestamp ? 1 : x.timestamp > y.timestamp ? -1 : x.event_id.localeCompare(y.event_id),
    );
  for (const e of combined) {
    if (seen.has(e.event_id)) continue;
    seen.add(e.event_id);
    out.push(e);
    if (out.length >= limit) break;
  }
  return out;
}

export async function listRecentOcsEvents(
  iccid: string,
  limit: number,
  eventTypes: readonly string[] | undefined,
  since: string | undefined,
  kv: OcsEventRoutingKv,
): Promise<ListRecentOcsEventsResult> {
  const routingRaw = (await kv.get(`iccid:${iccid}`, "json")) as IccidRoutingEntry | null;
  const resellerId = routingRaw?.reseller_id ?? LIFECYCLE_RESELLER_ID;

  async function collectPrefix(itemPrefix: string): Promise<OcsEventStored[]> {
    const fromItems: OcsEventStored[] = [];
    let listCursor: string | undefined;
    do {
      const listed = await kv.list({ prefix: itemPrefix, cursor: listCursor });
      for (const k of listed.keys) {
        const raw = await kv.get(k.name, "text");
        if (!raw) continue;
        try {
          fromItems.push(JSON.parse(raw) as OcsEventStored);
        } catch {
          // skip corrupt entries
        }
      }
      listCursor = listed.list_complete ? undefined : listed.cursor;
    } while (listCursor !== undefined);
    return fromItems;
  }

  const ringKey = `events:${resellerId}:${iccid}`;
  const fromItems = await collectPrefix(`${ringKey}:evt:`);
  // Prod wrote unrouted events under reseller 0 until 2026-09-10. Keep reading
  // that prefix so yesterday's location buffer is not orphaned after the default
  // moved to 1170.
  const fromLegacyZero =
    resellerId === 0 ? [] : await collectPrefix(`events:0:${iccid}:evt:`);

  const legacy = (await kv.get(ringKey, "json")) as StoredEventBatch | null;
  const legacyZero =
    resellerId === 0 ? null : ((await kv.get(`events:0:${iccid}`, "json")) as StoredEventBatch | null);

  const merged = new Map<string, OcsEventStored>();
  for (const e of legacy?.events ?? []) {
    merged.set(e.event_id, e);
  }
  for (const e of fromItems) {
    merged.set(e.event_id, e);
  }
  // Legacy events:0 must fill gaps only. Same event_id after a 24h TTL
  // re-ingest would otherwise overwrite the 1170 copy that carries
  // account_id / reseller_id / subscriber_id.
  for (const e of fromLegacyZero) {
    if (!merged.has(e.event_id)) merged.set(e.event_id, e);
  }
  for (const e of legacyZero?.events ?? []) {
    if (!merged.has(e.event_id)) merged.set(e.event_id, e);
  }

  const allSorted = [...merged.values()].sort((a, b) => {
    if (a.occurred_at !== b.occurred_at) return a.occurred_at - b.occurred_at;
    return a.event_id.localeCompare(b.event_id);
  });

  const totalInBuffer = allSorted.length;
  const bufferOldest =
    allSorted.length > 0 ? new Date(allSorted[0]!.occurred_at * 1000).toISOString() : null;
  const bufferNewest =
    allSorted.length > 0
      ? new Date(allSorted[allSorted.length - 1]!.occurred_at * 1000).toISOString()
      : null;

  const sinceMs = since ? new Date(since).getTime() : null;

  const filtered = allSorted.filter((e) => {
    if (sinceMs !== null && e.occurred_at * 1000 <= sinceMs) return false;
    if (eventTypes && eventTypes.length > 0 && !eventTypes.includes(e.event_type)) return false;
    return true;
  });

  const filteredCount = filtered.length;

  const sliced = filtered.slice(-limit).reverse();

  const events: OcsEventOutput[] = sliced.map((e) => {
    const out: OcsEventOutput = {
      event_id: e.event_id,
      event_type: e.event_type,
      timestamp: new Date(e.occurred_at * 1000).toISOString(),
      data: e.payload,
    };
    if (e.subscriber_id !== undefined) out.subscriber_id = e.subscriber_id;
    if (e.account_id !== undefined) out.account_id = e.account_id;
    if (e.reseller_id !== undefined) out.reseller_id = e.reseller_id;
    return out;
  });

  return {
    iccid,
    events,
    total_in_buffer: totalInBuffer,
    filtered_count: filteredCount,
    buffer_oldest_event_timestamp: bufferOldest,
    buffer_newest_event_timestamp: bufferNewest,
  };
}
